import React, { Children, useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

import { LegacyIconButton } from '../../navigation/LegacyIconButton';

import { useUnscrollableBody } from './useUnscrollableBody';
import { useFocusTrap } from './useFocusTrap';
import styles from './Modal.module.scss';

type Props = {
  /**
   * The description of the modal modal to be read by screen readers when `Modal` opens.
   */
  'aria-label'?: string;
  /**
   * Indicates whether `Modal` can be closed. When `false`,
   * no close button will render and clicking the overlay will not
   * close `Modal`. The code rendering `Modal` must manually close it.
   */
  canClose?: boolean;
  /**
   * Indicates whether `document.body` can still be scrolled.
   */
  canScrollBody?: boolean;
  /**
   * The content to render inside of `Modal`.
   */
  children: React.JSX.Element;
  /**
   * Function called when the modal has finished closing.
   */
  onClosed: () => void;
};

export const Modal = ({
  'aria-label': ariaLabel = undefined,
  canClose = true,
  canScrollBody,
  children,
  onClosed,
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  useUnscrollableBody({ shouldAllowScroll: canScrollBody === true });

  // Handle close request and kick off closing animation.
  const onClose = () => {
    if (canClose) {
      setIsOpen(false);
    }
  };

  // Once the closing animation has completed, call `onClosed`.
  const onAnimationEnd = () => {
    if (isOpen === false) {
      onClosed();
    }
  };

  // Close `Modal` when escape key is pressed. No need
  // to worry about `canClose` here since it's handled elsewhere.
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keyup', onEscape, { capture: true });
    return () => {
      window.removeEventListener('keyup', onEscape, { capture: true });
    };
  });

  // Keep focus to nodes within the modal.
  useFocusTrap(modalRef);

  const overlay = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      data-testid="ModalOverlay"
      onClick={onClose}
    />
  );

  const closeButton = !!canClose && (
    <LegacyIconButton
      aria-label="Close modal"
      color="icon-ghost"
      icon="formX"
      onClick={onClose}
      size="medium"
    />
  );

  // Pass `closeButton` as a prop to the only child. This allows
  // the child to render `closeButton` wherever it wants while still
  // managing styling and behavior within `Modal`, and it sidesteps
  // requiring a function as a child.
  const onlyChild = children && Children.only(children);
  const modalChildren = {
    ...onlyChild,
    props: {
      ...(children.props || {}),
      closeButton,
    },
  };

  const modal = (
    <div
      aria-label={ariaLabel}
      aria-live="polite"
      aria-modal="true"
      className={styles.modal}
      ref={modalRef}
      role="dialog"
      data-zds
    >
      {modalChildren}
    </div>
  );

  return (
    <div
      className={clsx(styles.root, isOpen ? styles.rootIn : styles.rootOut)}
      data-testid="Modal"
      onAnimationEnd={onAnimationEnd}
      data-zds
    >
      {overlay}
      {modal}
    </div>
  );
};
